import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { CheckCircle, AlertTriangle, XCircle, Info, X } from 'lucide-react';

/* ════════════════════════════════════════════════════════════
   TOAST SYSTEM — Rectorium
   Usage :
     const toast = useToast();
     toast.success('Title', 'Detail');
     toast.error('Error Title', 'Error Detail');
     toast.warn('Warning Title', 'Warning Detail');
     toast.info('Info Title', 'Info Detail');
   ════════════════════════════════════════════════════════════ */

const ToastContext = createContext(null);

const ICONS = {
  success: CheckCircle,
  error:   XCircle,
  warn:    AlertTriangle,
  info:    Info,
};

const COLORS = {
  success: { bg: 'var(--success-bg)', border: 'var(--success)', color: 'var(--success)' },
  error:   { bg: 'var(--danger-bg)',  border: 'var(--danger)',  color: 'var(--danger)'  },
  warn:    { bg: 'var(--warn-bg)',    border: 'var(--warn)',    color: 'var(--warn)'    },
  info:    { bg: 'var(--info-bg)',    border: 'var(--info)',    color: 'var(--info)'    },
};

let nextId = 0;

const Toast = ({ id, type, title, message, onDismiss }) => {
  const Icon = ICONS[type] || Info;
  const c    = COLORS[type] || COLORS.info;

  return (
    <div
      className="rectorium-toast"
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: '14px 16px', borderRadius: 12,
        background: 'var(--surface)',
        border: `1px solid ${c.border}`,
        boxShadow: 'var(--shadow-lg)',
        minWidth: 'min(300px, calc(100vw - 32px))',
        maxWidth: 'min(420px, calc(100vw - 32px))',
        boxSizing: 'border-box',
        animation: 'toastIn 0.25s cubic-bezier(0.34,1.56,0.64,1)',
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Barre colorée à gauche */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: c.color, borderRadius: '12px 0 0 12px' }} />

      <Icon size={18} style={{ color: c.color, flexShrink: 0, marginTop: 1 }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        {title && <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', marginBottom: message ? 2 : 0 }}>{title}</div>}
        {message && <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5 }}>{message}</div>}
      </div>

      <button
        onClick={() => onDismiss(id)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-3)', padding: 2, flexShrink: 0 }}
      >
        <X size={14} />
      </button>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    clearTimeout(timers.current[id]);
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const show = useCallback((type, titleOrMsg, message, duration = 4000) => {
    const id = ++nextId;
    const isSimple = !message;
    const toast = {
      id, type,
      title:   isSimple ? undefined : titleOrMsg,
      message: isSimple ? titleOrMsg : message,
    };
    setToasts(prev => [...prev.slice(-4), toast]); // max 5 toasts
    if (duration > 0) {
      timers.current[id] = setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  // Objet STABLE : il ne dépend que de `show` et `dismiss`, eux-mêmes stables. Recréé à chaque
  // rendu, chaque toast affiché (puis retiré) donnait un nouveau `toast` à ~20 écrans qui le
  // mettent dans les dépendances de leur `load` : un échec ou un dépassement de délai relançait
  // le chargement, qui échouait, qui affichait un toast… (incident du 2026-10-12).
  const api = useMemo(() => ({
    success: (msg, detail, dur) => show('success', msg, detail, dur),
    error:   (msg, detail, dur) => show('error',   msg, detail, dur ?? 6000),
    warn:    (msg, detail, dur) => show('warn',     msg, detail, dur),
    info:    (msg, detail, dur) => show('info',     msg, detail, dur),
    dismiss,
  }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}

      {/* Conteneur des toasts */}
      <div className="rectorium-toast-container" style={{
        position: 'fixed', bottom: 24, right: 24,
        display: 'flex', flexDirection: 'column-reverse', gap: 10,
        zIndex: 99999, pointerEvents: 'none',
      }}>
        {toasts.map(t => (
          <div key={t.id} style={{ pointerEvents: 'auto' }}>
            <Toast {...t} onDismiss={dismiss} />
          </div>
        ))}
      </div>

      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateX(100%) scale(0.9); }
          to   { opacity: 1; transform: translateX(0)   scale(1);   }
        }
        @media (max-width: 480px) {
          .rectorium-toast-container {
            left: 12px !important;
            right: 12px !important;
            bottom: calc(12px + env(safe-area-inset-bottom, 0px)) !important;
          }
          .rectorium-toast {
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
          }
        }
      `}</style>
    </ToastContext.Provider>
  );
};

const FALLBACK_TOAST = {
  success: () => {},
  error: () => {},
  warn: () => {},
  info: () => {},
  show: () => {},
  remove: () => {},
};

export { ToastContext };

// eslint-disable-next-line react-refresh/only-export-components
export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) return FALLBACK_TOAST;
  return ctx;
};
